import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ISpecialGames, SPECIAL_GAME_MODE_FLAGS } from '@GFTypes/GameStoreTypes';
import { ESpecialGamesType } from '@GFTypes/SpecialGames';

export function getSpecialGameDataByType(obj: ISpecialGames | undefined, type: ESpecialGamesType): ISpecialGames | undefined {
    if (!obj) return undefined;

    if (obj.type === type) {
        return obj;
    }

    return getSpecialGameDataByType(obj.specialGames, type);
}

export function getSpecialGames(gameStore: GameStore): ISpecialGames | undefined {
    const specialGames = gameStore.get(EGameStoreKeys.SpinOutcome)?.specialGames;
    const resumedSpecialGames = gameStore.get(EGameStoreKeys.ResumedSpinOutcome)?.specialGames;
    const currentSpecialGames = specialGames || resumedSpecialGames;
    return currentSpecialGames;
}

export function getLastNotEndedSpecialGames(gameStore: GameStore): ISpecialGames | undefined {
    const currentSpecialGames = getSpecialGames(gameStore);
    if (!currentSpecialGames) return;
    let current = currentSpecialGames;
    while (current.specialGames) {
        if (current.type === ESpecialGamesType.FREE_GAMES && isLastFreeGamesSpin(gameStore)) break;
        if (current.type === ESpecialGamesType.LOCK_AND_ROLL && isLastLockAndRollSpin(gameStore)) break;
        current = current.specialGames;
    }
    return currentSpecialGames;
}

function isLastFreeGamesSpin(gameStore: GameStore): boolean {
    const { totalSpins, currentSpin } = gameStore.get(EGameStoreKeys.FreeGames);
    const isFreeGamesMode = gameStore.get(EGameStoreKeys.IsFreeGamesMode);
    const isLastFreeGamesSpin = currentSpin === totalSpins;
    return isFreeGamesMode && isLastFreeGamesSpin;
}

export function isLastLockAndRollSpin(gameStore: GameStore): boolean {
    const { totalSpins } = gameStore.get(EGameStoreKeys.LockAndRoll);
    const isLockAndRollMode = gameStore.get(EGameStoreKeys.IsLockAndRollMode);
    const isLastLockAndRollSpin = totalSpins === 0;
    return isLockAndRollMode && isLastLockAndRollSpin;
}

export function isLastSpecialGamesSpin(gameStore: GameStore): boolean {
    return isLastFreeGamesSpin(gameStore) || isLastLockAndRollSpin(gameStore);
}

export function isBeforeFirstFreeGamesSpin(gameStore: GameStore): boolean {
    const { currentSpin, totalSpins } = gameStore.get(EGameStoreKeys.FreeGames);
    const isBeforeFirstFreeGamesSpin = currentSpin === 0 && totalSpins > 0;
    return isBeforeFirstFreeGamesSpin;
}

export function isBeforeFirstLockAndRollSpin(gameStore: GameStore): boolean {
    const { currentSpin, totalSpins } = gameStore.get(EGameStoreKeys.LockAndRoll);
    const isBeforeFirstLockAndRollSpin = currentSpin === 0 && totalSpins > 0;
    return isBeforeFirstLockAndRollSpin;
}

export function isBeforeFirstSpecialGamesSpin(gameStore: GameStore): boolean {
    return isBeforeFirstFreeGamesSpin(gameStore) || isBeforeFirstLockAndRollSpin(gameStore);
}

export function isSpecialGamesTrigger(gameStore: GameStore): boolean {
    const currentSpecialGames = getSpecialGames(gameStore);
    if (!currentSpecialGames) return false;
    const mode = SPECIAL_GAME_MODE_FLAGS.get(currentSpecialGames?.type)!;
    return (
        !!currentSpecialGames?.triggerPositions?.length &&
        currentSpecialGames?.triggerPositions?.length > 0 &&
        !gameStore.get(mode) &&
        currentSpecialGames.currentSpin === 0
    );
}
